import { useEffect, useState } from "react";
import {
  Calendar, User, Path, WarningCircle, CheckCircle, Clock, Package, Note,
  ArrowRight,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";

const PHASE_LABEL_MAP = {
  development: "development", drilling: "drilling", hauling: "hauling",
  ground_support: "ground_support", services: "services",
  ventilation: "ventilation", other: "other",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch (_e) { return iso; }
}

function daysBetween(a, b) {
  if (!a || !b) return 0;
  return Math.max(1, Math.ceil((new Date(b) - new Date(a)) / 86400000));
}

function statusBadge(status, t) {
  if (!status) return null;
  const map = {
    delayed:  { cls: "bg-red-100 text-red-700 border-red-200",       icon: WarningCircle, label: t("taskStatusDelayed") },
    at_risk:  { cls: "bg-amber-100 text-amber-700 border-amber-200", icon: WarningCircle, label: t("taskStatusAtRisk") },
    early:    { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle, label: t("taskStatusEarly") },
    on_track: { cls: "bg-blue-50 text-blue-900 border-blue-200",    icon: Clock,          label: t("taskStatusOnTrack") },
  };
  const s = map[status.kind || status] || map.on_track;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${s.cls}`}>
      <Icon size={12} weight="fill" /> {s.label}
      {typeof status.days === "number" && status.days > 0 && (
        <span className="opacity-80">· {status.days}d</span>
      )}
    </span>
  );
}

export default function TaskDetailDialog({ task, allTasks, open, onOpenChange, taskStatus }) {
  const { t } = useLang();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (!open || !task) return;
    setLoadingHistory(true);
    api.get(`/tasks/${task.id}/progress-history`)
      .then((r) => setHistory(r.data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, [open, task]);

  if (!task) return null;

  const byId = Object.fromEntries((allTasks || []).map((x) => [x.id, x]));
  // Support both legacy string deps and structured {p,t,l} deps.
  const depObjs = (task.dependencies || []).map((d) => (
    typeof d === "string" ? { p: d, t: "FS", l: 0 } : { p: d.p, t: d.t || "FS", l: Number(d.l) || 0 }
  ));
  const deps = depObjs.map((d) => ({ ...d, task: byId[d.p] })).filter((d) => d.task);
  const dependents = (allTasks || []).filter((x) => {
    const others = (x.dependencies || []).map((d) => (typeof d === "string" ? d : d.p));
    return others.includes(task.id);
  });
  const duration = daysBetween(task.start_date, task.end_date);
  const phaseLabel = PHASE_LABEL_MAP[task.phase] ? t(PHASE_LABEL_MAP[task.phase]) : task.phase;
  const resources = task.resources || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="task-detail-dialog" className="bg-white max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="font-heading text-slate-900 text-lg leading-tight">
            {task.name}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 mt-1 text-xs">
            <span className="text-slate-500 uppercase tracking-wider font-semibold">{phaseLabel}</span>
            {taskStatus && statusBadge(taskStatus, t)}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] px-6 py-4">
          {/* Grid summary */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="border border-slate-200 rounded-md p-3">
              <p className="overline-label flex items-center gap-1"><Calendar size={11} /> {t("startDate")}</p>
              <p className="font-mono text-sm text-slate-900 mt-1" data-testid="task-detail-start">{fmtDate(task.start_date)}</p>
            </div>
            <div className="border border-slate-200 rounded-md p-3">
              <p className="overline-label flex items-center gap-1"><Calendar size={11} /> {t("endDate")}</p>
              <p className="font-mono text-sm text-slate-900 mt-1" data-testid="task-detail-end">{fmtDate(task.end_date)}</p>
            </div>
            <div className="border border-slate-200 rounded-md p-3">
              <p className="overline-label flex items-center gap-1"><Clock size={11} /> {t("duration")}</p>
              <p className="font-heading font-black text-lg mt-1" data-testid="task-detail-duration">{duration} <span className="text-xs font-normal text-slate-500">{t("days")}</span></p>
            </div>
            <div className="border border-slate-200 rounded-md p-3">
              <p className="overline-label flex items-center gap-1"><User size={11} /> {t("assignee")}</p>
              <p className="text-sm text-slate-900 mt-1 truncate" data-testid="task-detail-assignee">{task.assignee || "—"}</p>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-1.5">
              <p className="overline-label">{t("progress")}</p>
              <p className="font-heading font-black text-lg text-blue-900" data-testid="task-detail-progress">{task.progress}%</p>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-900" style={{ width: `${task.progress}%` }} />
            </div>
          </div>

          {/* Resources */}
          <section className="mb-5" data-testid="task-detail-resources">
            <div className="flex items-center gap-2 mb-2">
              <Package size={14} weight="duotone" className="text-blue-900" />
              <p className="font-heading font-bold text-sm text-slate-900">{t("assignedResources")}</p>
              <span className="text-xs text-slate-400">({resources.length})</span>
            </div>
            {resources.length === 0 ? (
              <p className="text-sm text-slate-400 italic pl-6">{t("noResourcesAssigned")}</p>
            ) : (
              <ul className="pl-6 space-y-1.5">
                {resources.map((r, idx) => (
                  <li key={idx} data-testid={`task-detail-resource-${idx}`} className="text-sm text-slate-700 flex flex-wrap items-baseline gap-1.5">
                    <span className="font-semibold">{r.name || "—"}</span>
                    {(r.quantity || r.unit) && (
                      <span className="font-mono text-xs text-slate-500">· {r.quantity || 1} {r.unit || ""}</span>
                    )}
                    {r.note && <span className="text-xs text-slate-500 italic">— {r.note}</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Dependencies */}
          <section className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Path size={14} weight="duotone" className="text-blue-900" />
              <p className="font-heading font-bold text-sm text-slate-900">{t("dependencies")}</p>
            </div>
            <div className="pl-6 grid sm:grid-cols-2 gap-3">
              <div>
                <p className="overline-label mb-1">{t("dependsOn")}</p>
                {deps.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">{t("none")}</p>
                ) : (
                  <ul className="space-y-0.5">
                    {deps.map((d) => (
                      <li key={d.p} className="text-sm text-slate-700 flex items-center gap-1" data-testid={`task-detail-dep-${d.p}`}>
                        <ArrowRight size={12} className="text-slate-400" />
                        {d.task.name}
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 ml-1">{d.t}</span>
                        {d.l ? <span className="text-[10px] font-mono text-slate-500">{d.l > 0 ? `+${d.l}d` : `${d.l}d`}</span> : null}
                        <span className="text-xs text-slate-400">({d.task.progress}%)</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="overline-label mb-1">{t("blocks")}</p>
                {dependents.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">{t("none")}</p>
                ) : (
                  <ul className="space-y-0.5">
                    {dependents.map((d) => (
                      <li key={d.id} className="text-sm text-slate-700 flex items-center gap-1">
                        <ArrowRight size={12} className="text-slate-400" /> {d.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </section>

          {/* Progress history */}
          <section data-testid="task-detail-history">
            <div className="flex items-center gap-2 mb-2">
              <Note size={14} weight="duotone" className="text-blue-900" />
              <p className="font-heading font-bold text-sm text-slate-900">{t("progressHistory")}</p>
              <span className="text-xs text-slate-400">({history.length})</span>
            </div>
            {loadingHistory ? (
              <p className="text-xs text-slate-400 pl-6">{t("loading")}</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-400 italic pl-6">{t("noProgressHistory")}</p>
            ) : (
              <ol className="pl-6 border-l-2 border-slate-100 space-y-2">
                {history.map((h, idx) => (
                  <li key={idx} className="relative">
                    <span className="absolute -left-[9px] top-1 h-3 w-3 rounded-full bg-blue-900 border-2 border-white" />
                    <div className="text-xs text-slate-500">
                      {fmtDate(h.updated_at)}
                      {h.updated_by && <span> · {h.updated_by}</span>}
                    </div>
                    <div className="text-sm text-slate-800 font-semibold">{h.progress}%</div>
                    {h.note && <div className="text-xs text-slate-600 italic mt-0.5">{h.note}</div>}
                  </li>
                ))}
              </ol>
            )}
          </section>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
